import { Button, Empty, Flex, Modal, Spin } from 'antd';
import axios from 'axios';
import type { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import ArrowRightIcon from '../../../icons/arrow-right-icon.svg';
import { NavButton } from './nav-button';
import { localize } from '/react4xp/mixins/helpers';

type Locale = 'RU' | 'KK' | 'QQ' | 'EN';

type DayEventDto = {
    eventDate: string;
    formCode: string;
    text: string;
    title: string;
};

type Props = {
    selectedDay: Dayjs;
    selectDay: (day: Dayjs) => void;
    translations: Record<string, string>;
    lang: string;
};

const mapLangToLocale = (lang: string): Locale => {
    const l = String(lang || '').toUpperCase();
    if (l.startsWith('KK')) return 'KK';
    if (l.startsWith('EN')) return 'EN';
    if (l.startsWith('QQ')) return 'QQ';
    return 'RU';
};

export function CalendarEvents({ selectedDay, selectDay, translations, lang }: Props) {
    const [events, setEvents] = useState<DayEventDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const locale = useMemo(() => mapLangToLocale(lang), [lang]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            try {
                const res = await axios.get('/services/isnaportal/api/open-api/tax-calendar/day', {
                    params: {
                        day: selectedDay.date(),
                        month: selectedDay.month() + 1,
                        year: selectedDay.year(),
                        locale,
                    },
                });

                const data = Array.isArray(res.data) ? res.data : [];
                if (!cancelled) setEvents(data);
            } catch (e) {
                console.error(e);
                if (!cancelled) setEvents([]);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [selectedDay, locale]);

    const selectPrevDay = () => selectDay(selectedDay.subtract(1, 'day'));
    const selectNextDay = () => selectDay(selectedDay.add(1, 'day'));

    return (
        <div className="events">
            <Flex gap={10} className="title">
                <NavButton variant="white" direction="left" onClick={selectPrevDay} />
                <NavButton variant="white" direction="right" onClick={selectNextDay} />
                <p className="title__date">{selectedDay?.format('DD MMMM')}</p>
            </Flex>

            <Spin spinning={isLoading}>
                {events?.length !== 0 && (
                    <ul className="events__list">
                        {events.map((event, idx) => (
                            <EventListItem key={`${event.formCode}-${event.eventDate}-${idx}`} event={event} translations={translations} />
                        ))}
                    </ul>
                )}
            </Spin>

            {!isLoading && events?.length === 0 && (
                <Flex justify="center" align="center" style={{ paddingTop: 40 }}>
                    <Empty />
                </Flex>
            )}
        </div>
    );
}

const EventListItem = ({ event, translations }: { event: DayEventDto; translations: Record<string, string> }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    return (
        <li className="events__list__item">
            <p className="title">{event.title}</p>

            <div
                style={{ height: 110, overflow: 'hidden', wordBreak: 'break-word' }}
                dangerouslySetInnerHTML={{ __html: event.text }}
            />

            <div className="footer">
                <button className="footer__more-button" onClick={openModal}>
                    <p>{localize('more-detailed', translations)}</p>
                    <ArrowRightIcon />
                </button>
            </div>

            <Modal
                title={<div className="div__text">{event.title}</div>}
                open={isModalOpen}
                onCancel={closeModal}
                style={{ minWidth: '800px' }}
                footer={
                    <Flex justify="end" align="center" gap={20}>
                        <Button color="gold" onClick={closeModal} style={{ alignItems: 'center' }}>
                            {localize('button.close', translations)}
                        </Button>
                    </Flex>
                }
            >
                <div dangerouslySetInnerHTML={{ __html: event.text }} />
            </Modal>
        </li>
    );
};
